/**
 * @fileoverview useTranslation Hook
 * 
 * Main hook for accessing translations with interpolation and fallbacks.
 * Provides a React context-based translation system with TypeScript support.
 * 
 * @module i18n/hooks
 * @version 1.0.0
 * @since 2025-07-09
 */

import { useContext } from 'react';
import { I18nContext } from '../context/I18nProvider';
import { TranslationKey } from '../index';

// Interface pour les paramètres d'interpolation
export interface TranslationParams {
  [key: string]: string | number;
}

// Options pour la fonction de traduction
export interface TranslationOptions {
  fallback?: string;
  params?: TranslationParams;
}

/**
 * Hook principal pour accéder aux traductions
 * 
 * @returns Objet contenant la fonction de traduction et les utilitaires
 * 
 * @example
 * ```tsx
 * const { t, locale, setLocale } = useTranslation();
 * 
 * // Traduction simple
 * const title = t('screens.before.startMatch');
 * 
 * // Traduction avec interpolation
 * const message = t('timers.warmup', { duration: 5 });
 * 
 * // Traduction avec fallback personnalisé
 * const text = t('missing.key', { fallback: 'Default text' });
 * ```
 */
export const useTranslation = () => {
  const context = useContext(I18nContext);
  
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  
  const { locale, setLocale, messages, isLoading, error } = context;
  
  /**
   * Fonction de traduction avec support d'interpolation et fallbacks
   * 
   * @param key - Clé de traduction (ex: 'screens.before.startMatch')
   * @param options - Options incluant params et fallback
   * @returns Texte traduit avec interpolation appliquée
   */
  const t = (key: TranslationKey, options: TranslationOptions = {}): string => {
    const { params, fallback } = options;
    
    // Récupérer la traduction depuis le chemin de clé
    const translation = getNestedValue(messages, key);
    
    // Utiliser la traduction trouvée, le fallback, ou la clé elle-même
    let result = translation || fallback || key;
    
    // Si c'est la clé elle-même, logger en développement
    if (result === key && process.env.NODE_ENV === 'development') {
      //console.warn(`Translation missing for key: "${key}" in locale: "${locale}"`);
    }
    
    // Appliquer l'interpolation si des paramètres sont fournis
    if (params && typeof result === 'string') {
      result = interpolateString(result, params);
    }
    
    return result;
  };
  
  /**
   * Fonction de traduction avec pluralisation (pour usage futur)
   * 
   * @param key - Clé de traduction
   * @param count - Nombre pour déterminer la forme plurielle
   * @param options - Options additionnelles
   */
  const tp = (key: TranslationKey, count: number, options: TranslationOptions = {}): string => {
    const pluralKey = count === 1 ? `${key}.singular` : `${key}.plural`;
    return t(pluralKey as TranslationKey, { ...options, params: { ...options.params, count } });
  };
  
  return {
    // Fonction principale de traduction
    t,
    // Fonction de traduction avec pluralisation
    tp,
    // État actuel
    locale,
    setLocale,
    isLoading,
    error,
    // Utilitaires
    isReady: !isLoading && !error
  };
};

/**
 * Récupère une valeur nested depuis un objet via un chemin de clé
 * 
 * @param obj - Objet source
 * @param path - Chemin de clé (ex: 'screens.before.title')
 * @returns Valeur trouvée ou undefined
 */
const getNestedValue = (obj: any, path: string): string | undefined => {
  if (!obj || typeof path !== 'string') return undefined;
  
  return path.split('.').reduce((current, key) => {
    return current && typeof current === 'object' ? current[key] : undefined;
  }, obj);
};

/**
 * Remplace les placeholders dans une chaîne par les valeurs fournies
 * 
 * @param str - Chaîne avec placeholders (ex: "Hello {{name}}")
 * @param params - Paramètres de remplacement
 * @returns Chaîne avec placeholders remplacés
 * 
 * @example
 * interpolateString("Hello {{name}}", { name: "John" }) // "Hello John"
 * interpolateString("{{count}} items", { count: 5 }) // "5 items"
 */
const interpolateString = (str: string, params: TranslationParams): string => {
  return str.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const value = params[key];
    return value !== undefined ? String(value) : match;
  });
};

// Export du hook principal
export default useTranslation;